/**
 * Utility functions for brand/category-based mandatory fields
 * Data stored in attribute.data.mandatory_mapping
 *
 * Structure in data column:
 * {
 *   "mandatory_mapping": [
 *     { "brand": "Rare Rabbit", "category": "T-SHIRT", "mandatory": true },
 *     { "brand": "Rare Rabbit", "category": "SHIRT", "mandatory": true }
 *   ]
 * }
 */

import { datatypes } from "data/globalData";

export const MANDATORY_MAPPING_KEY = "mandatory_mapping";

/**
 * Check if attribute is mandatory based on brand and category
 * @param {Object} attribute - Attribute object with data.mandatory_mapping
 * @param {string} brand - Current brand value
 * @param {string} category - Current category value
 * @returns {boolean} - Whether field is mandatory
 */
export const isAttributeMandatory = (attribute, brand = "", category = "") => {
	// Check global NOTNULL constraint first (highest priority)
	if (attribute?.constraints?.NOTNULL) {
		return true;
	}

	// Check brand/category-based rules from data column
	const mandatoryMapping = attribute?.data?.[MANDATORY_MAPPING_KEY];

	if (!mandatoryMapping || !Array.isArray(mandatoryMapping) || mandatoryMapping.length === 0) {
		return false;
	}

	// Check if any rule matches current brand/category
	return mandatoryMapping.some((rule) => {
		const brandMatch = !rule.brand || rule.brand === brand;
		const categoryMatch = !rule.category || rule.category === category;
		return brandMatch && categoryMatch && rule.mandatory === true;
	});
};

/**
 * Get all mandatory attributes for given brand and category
 * @param {Array} attributes - Array of attribute objects
 * @param {string} brand - Current brand value
 * @param {string} category - Current category value
 * @returns {Array} - Array of mandatory attribute keys
 */
export const getMandatoryAttributes = (attributes, brand, category) => {
	return attributes.filter((attr) => isAttributeMandatory(attr, brand, category)).map((attr) => attr.key);
};

/**
 * Validate form data against mandatory rules
 * @param {Object} formData - Form data object (e.g., newStyleDetails)
 * @param {Array} attributes - Array of attribute objects
 * @param {string} brand - Current brand value
 * @param {string} category - Current category value
 * @param {string} linkedTable - Linked table name (e.g., 'style', 'style_details')
 * @returns {Object} - { isValid: boolean, missingFields: Array }
 */
export const validateMandatoryFields = (formData, attributes, brand, category, linkedTable) => {
	const missingFields = [];

	attributes.forEach((attr) => {
		if (isAttributeMandatory(attr, brand, category)) {
			const value = linkedTable ? formData[linkedTable]?.[attr.key] : formData[attr.key];

			let isEmpty = false;
			if (value === null || value === undefined || value === "") {
				isEmpty = true;
			} else if (Array.isArray(value) && value.length === 0) {
				isEmpty = true;
			} else if (
				(attr.data_type === datatypes.NUMBER.key && isNaN(value)) ||
				(attr.data_type === datatypes.DECIMAL.key && isNaN(value))
			) {
				isEmpty = true;
			}

			if (isEmpty) {
				missingFields.push(attr);
			}
		}
	});

	return {
		isValid: missingFields.length === 0,
		missingFields,
	};
};

/**
 * Add or update mandatory mapping for an attribute
 * @param {Object} attribute - Attribute object
 * @param {string} brand - Brand name
 * @param {string} category - Category name
 * @param {boolean} mandatory - Is mandatory flag
 * @returns {Object} - Updated attribute data object
 */
export const addMandatoryMapping = (attribute, brand, category, mandatory) => {
	const currentData = attribute?.data || {};
	const currentMapping = currentData[MANDATORY_MAPPING_KEY] || [];

	// Check if rule already exists
	const existingIndex = currentMapping.findIndex((rule) => rule.brand === brand && rule.category === category);

	let updatedMapping;
	if (existingIndex >= 0) {
		// Update existing rule
		updatedMapping = [...currentMapping];
		updatedMapping[existingIndex] = { brand, category, mandatory };
	} else {
		// Add new rule
		updatedMapping = [...currentMapping, { brand, category, mandatory }];
	}

	return {
		...currentData,
		[MANDATORY_MAPPING_KEY]: updatedMapping,
	};
};

/**
 * Remove mandatory mapping for an attribute
 * @param {Object} attribute - Attribute object
 * @param {string} brand - Brand name
 * @param {string} category - Category name
 * @returns {Object} - Updated attribute data object
 */
export const removeMandatoryMapping = (attribute, brand, category) => {
	const currentData = attribute?.data || {};
	const currentMapping = currentData[MANDATORY_MAPPING_KEY] || [];

	const updatedMapping = currentMapping.filter((rule) => !(rule.brand === brand && rule.category === category));

	return {
		...currentData,
		[MANDATORY_MAPPING_KEY]: updatedMapping,
	};
};

/**
 * Bulk import mandatory mappings from Excel/CSV data
 * @param {Array} importData - Array of { attribute_key, brand, category, mandatory }
 * @param {Array} attributes - Current attributes array
 * @returns {Array} - Updated attributes array
 */
export const bulkImportMandatoryMappings = (importData, attributes) => {
	const mappingsByAttr = importData.reduce((acc, row) => {
		if (!acc[row.attribute_key]) {
			acc[row.attribute_key] = [];
		}
		acc[row.attribute_key].push({
			brand: row.brand,
			category: row.category,
			mandatory: row.mandatory === "Mandatory" || row.mandatory === true,
		});
		return acc;
	}, {});

	return attributes.map((attr) => {
		if (mappingsByAttr[attr.key]) {
			return {
				...attr,
				data: {
					...attr.data,
					[MANDATORY_MAPPING_KEY]: mappingsByAttr[attr.key],
				},
			};
		}
		return attr;
	});
};
